import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService, USER_GROUPS, type User } from '../src/services/authService';
import { AWARD_CATEGORIES } from '../src/config/2025/appConfig';
import { applicationService } from '../src/services/applicationService';

interface CategoryStats {
  category: string;
  label: string;
  labelChi: string;
  count: number;
}

const Dashboard: React.FC = () => {
  const [categoryStats, setCategoryStats] = useState<CategoryStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
    const currentUser = authService.getCurrentUserFromCache();
    setUser(currentUser);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Get all applications to count by category
      const applications = await applicationService.getApplications();
      
      // Filter for current year (2025) applications only
      const currentYearApplications = applications.filter(app => 
        !app.applicationYear || app.applicationYear === '2025'
      );
      
      // Create category stats with counts (always show all categories)
      const stats = AWARD_CATEGORIES.map(category => {
        const count = currentYearApplications.filter(app => app.applicationDetail?.awardCategory === category.value).length;
        return {
          category: category.value,
          label: category.label,
          labelChi: category.labelChi,
          count
        };
      });

      // Sort by count (highest first)
      stats.sort((a: CategoryStats, b: CategoryStats) => b.count - a.count);
      
      setCategoryStats(stats);
          } catch (error) {
        // Show all categories with 0 count when there's an error
      const stats = AWARD_CATEGORIES.map(category => ({
        category: category.value,
        label: category.label,
        labelChi: category.labelChi,
        count: 0
      }));
      setCategoryStats(stats);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (categoryValue: string) => {
    // Navigate to applications page with category filter
    navigate(`/backend/applications?category=${categoryValue}`);
  };

  const getTotalApplications = () => {
    return categoryStats.reduce((total, stat) => total + stat.count, 0);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Dashboard
        </Typography>
        <Typography>Loading award categories...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Dashboard
      </Typography>

      {/* Total Applications Summary */}
      <Paper sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        mb: 3, 
        backgroundColor: 'primary.main', 
        color: 'white' 
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 0 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AssignmentIcon sx={{ 
              mr: { xs: 1, sm: 2 },
              fontSize: { xs: '1.5rem', sm: '2rem' }
            }} />
            <Box>
              <Typography variant="h4" sx={{ 
                fontSize: { xs: '1.75rem', sm: '2.125rem' }
              }}>
                {getTotalApplications()}
              </Typography>
              <Typography variant="body2" sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}>
                Total Applications (2025)
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Award Categories List */}
      <Paper sx={{ 
        height: { xs: '60vh', sm: '70vh', md: 'calc(100vh - 400px)' },
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          flexShrink: 0
        }}>
          <Typography variant="h6">
            Applications by Award Category
          </Typography>
        </Box>
        <Box sx={{ 
          flexGrow: 1,
          overflow: 'auto',
          minHeight: 0
        }}>
          <List>
            {categoryStats.map((stat, index) => (
              <React.Fragment key={stat.category}>
                <ListItem disablePadding>
                  <ListItemButton 
                    onClick={() => handleCategoryClick(stat.category)}
                    sx={{ 
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 1.5, sm: 2 },
                      '&:hover': { 
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%'
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: { xs: 0.5, sm: 1 }
                          }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 500,
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                              }}
                            >
                              {stat.label}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                              / {stat.labelChi}
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: { xs: 0.5, sm: 1 }
                          }}>
                            <Chip 
                              label={stat.count} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                              sx={{ 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                height: { xs: 24, sm: 28 }
                              }}
                            />
                            <ArrowForwardIcon 
                              fontSize="small" 
                              color="action"
                              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                            />
                          </Box>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < categoryStats.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Paper>

      {/* User info */}
      {user && (
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Your Information
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">
              <strong>Name:</strong> {user.fullName || 'N/A'}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {user.email}
            </Typography>
            <Chip
              label={authService.getUserHighestGroup(user)}
              color={
                authService.getUserHighestGroup(user) === USER_GROUPS.SUPER_ADMIN
                  ? 'error'
                  : authService.getUserHighestGroup(user) === USER_GROUPS.ADMIN
                  ? 'warning'
                  : 'info'
              }
              size="small"
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default Dashboard;